import {
  HTTP_FETCH_MODE,
  HTTP_FETCH_CREDENTIALS,
  HTTP_FETCH_CACHE_MODE,
  HTTP_FETCH_REDIRECT,
  HTTP_FETCH_REFERRER_POLICY,
  SPACE
} from '../../constants'
import { jsonp, logger } from '../../utils'

type Data = Record<string, string>

export type METHOD = 'GET' | 'POST'| 'PUT'| 'PATCH' | 'DELETE'
export const CONTENT_TYPE = 'Content-Type'

export const enum MIME_TYPE {
  JSON = 'application/json',
  URLENCODED = 'application/x-www-form-urlencoded',
  MULTIPART = 'multipart/form-data'
}

// Global headers
const key = 'Authorization'
const headers = new Headers
const trimSlashes = /^\/|\/$/g

class Api {
  headers: Headers
  #pathname: string
  origin!: string
  version?: string

  baseUrl?: string

  public useToken(token: string) {
    const bearer = `Bearer ${token}`
    this.headers.set(key, bearer)
  }

  public static useToken = useToken

  constructor(pathname = '', headers?: Data) {
    this.#pathname = pathname.replace(trimSlashes, SPACE)
    this.headers = new Headers(headers)
  }

  get url() {
    return this.baseUrl && this.baseUrl.startsWith('http') ?
      this.baseUrl
      : (this.#pathname + (this.version ? `/${this.version}` : SPACE))
  }

  fetchJSONP<T>(endpoint: string, query: Record<string, string | number> = {}, log = false) {
    const url = new URL(endpoint)
    Object.keys(query).forEach(key => url.searchParams.set(key, String(query[key])))

    if (log) logger.VIOLET.FILL('API', '[fetchJSONP]', { url: url.toString(), query })

    return jsonp<T>(url.toString())
  }

  fetch<T>(endpoint: string, method: METHOD = 'GET', data?: T, options?: FetchOptions): Promise<Response> {
    try {
      let body: string | FormData | null = null

      const path = `${this.url}/${endpoint.replace(trimSlashes, SPACE)}`.replace(trimSlashes, SPACE)

      const fetchOptions = {
        method,
        ...options,
        headers: options?.headers instanceof Headers ?
          options.headers :
          new Headers(options?.headers)
      }

      let url = new URL(this.origin || location.origin)

      if (this.baseUrl) {
        url = new URL(path)
      } else {
        url.pathname = path
      }

      this.headers.forEach((value, key) => fetchOptions.headers.set(key, value))

      if (!fetchOptions.headers.has('authorization')) {
        headers.forEach((value, key) => fetchOptions.headers.set(key, value))
      } else if (!fetchOptions.headers.get('authorization')) {
        fetchOptions.headers.delete('authorization')
      }

      if (method.toUpperCase() === 'GET' && typeof data === 'object') {
        Object.keys(data as never).filter(Boolean).forEach(key => {
          if (data && data[key as never] != null) url.searchParams.set(key, encodeURIComponent(data[key as never]))
        })
      }

      const contentType = fetchOptions.headers.get(CONTENT_TYPE)

      if (contentType === 'application/json' && typeof data !== 'string') {
        body = JSON.stringify(data)
      } else if (contentType === MIME_TYPE.MULTIPART && !(data instanceof FormData) && typeof data === 'object') {
        body = new FormData()

        Object.keys(data as Data).forEach(key => {
          (<FormData>body).append(key, (<Data>data)[key] as string)
        })
      }

      if ((<METHOD[]>['POST', 'PUT', 'PATCH']).includes(method.toUpperCase() as METHOD) && body) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (<any>fetchOptions)['body'] = body
      }

      return window.fetch(url.toString(), fetchOptions)
    } catch (error) {
      return Promise.resolve(error)
    }
  }
}

function useToken(token: string) {
  if (!token) return
  const bearer = `Bearer ${token}`
  headers.set(key, bearer)
}


export interface FetchOptions {
  method?: METHOD
  mode?: HTTP_FETCH_MODE
  cache?: HTTP_FETCH_CACHE_MODE
  credentials?: HTTP_FETCH_CREDENTIALS
  redirect?: HTTP_FETCH_REDIRECT
  referrerPolicy?: HTTP_FETCH_REFERRER_POLICY,
  headers?: Headers | Record<string, string>,
  params?: Record<string, unknown>
}

export {
  headers,
  Api,
  useToken
}
